import { getTypedValue } from "@coworker/apprestructured/src/shared/helpers/schemaValidator";
import { ViewerFragmentCommonProps } from "../ViewerFragment";
import {
  getByPath,
  setByPath,
} from "@coworker/apprestructured/src/shared/helpers/object.helper";
import { isLoading } from "@coworker/apprestructured/src/task-management/loading";
import { ItemNoAndTypeWithOrderedDetails } from "@coworker/apprestructured/src/task-management/types/TaskDetailTypes";
import {
  OrderedDetailsMode,
  ORDERED_DETAILS_MODES,
} from "@coworker/apprestructured/src/task-management/types/OrderedDetailsMode";
import { ListItem } from "../../../SelectListItem/SelectListItem";
import { EditArticlesOrderedDetails } from "../../../EditArticlesOrderedDetails/EditArticlesOrderedDetails";

export type ArticlesOrderedDetailsViewerFragmentConfigProps = {
  mode: OrderedDetailsMode["id"];
  field: string;
  visibility: VisibilityCondition;
};

type ArticlesOrderedDetailsViewerFragmentProps = {
  configProps: ArticlesOrderedDetailsViewerFragmentConfigProps;
  commonProps: ViewerFragmentCommonProps;
};

interface VisibilityCondition {
  field: string;
  value: any;
  operator: "in";
}

function shouldShowField(
  task: ViewerFragmentCommonProps["task"],
  visibility: VisibilityCondition
): boolean {
  const currentValue = getByPath(task, visibility.field) as ListItem | null;

  if (visibility.operator === "in") {
    return Array.isArray(visibility.value)
      ? visibility.value.includes(currentValue?.value)
      : false;
  }

  return true;
}

export const ArticlesOrderedDetailsViewerFragment = ({
  configProps,
  commonProps,
}: ArticlesOrderedDetailsViewerFragmentProps) => {
  const { field, mode, visibility } = configProps;
  const { task, onTaskChange, blockInput } = commonProps;
  const itemsWithOrderedDetails = getTypedValue<
    ItemNoAndTypeWithOrderedDetails[]
  >(getByPath(task, field));

  const modeConfig = ORDERED_DETAILS_MODES[mode];
  if (!modeConfig) {
    return null;
  }

  if (!shouldShowField(task, visibility)) {
    return null;
  }

  if (isLoading(itemsWithOrderedDetails)) {
    return null;
  }

  // ponytail: task can't be created with empty items, so empty here means the
  // articles were removed after creation -- render disabled with a 0 count
  // instead of hiding the section entirely, matching MediasQuantityViewerFragment.
  const hasItems =
    Array.isArray(itemsWithOrderedDetails) &&
    itemsWithOrderedDetails.length > 0;
  const orderedItems = hasItems ? itemsWithOrderedDetails : [];

  return (
    <EditArticlesOrderedDetails
      mode={modeConfig}
      itemNosAndTypeWithOrderedDetails={orderedItems}
      disabled={blockInput || !hasItems}
      onCompleteChange={(itemNo, field, completed) => {
        const updatedItems = orderedItems.map((item) =>
          item.no === itemNo
            ? { ...item, [field]: { ...item[field], completed } }
            : item
        );
        onTaskChange(setByPath("details.items", updatedItems));
      }}
    />
  );
};
